import { BigNumber } from '@frontend/numeric';

import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { ESymbolCategory, ISymbolDetail } from '@aqx-core/common/api/market';
import { EFillPolicy, EOrderExpiry, EOrderType } from '@aqx-core/common/api/order';
import { i18n } from '@aqx-core/common/i18n';

import { companyService } from '../../company';
import {
  getTradeBasePricesDataKeyCentroid,
  getTradeBasePricesDataKeyHorizon,
  getTradeBasePricesDataKeyMT4,
  getTradeBasePricesDataKeyMT5,
  getTradeControlsDataKeyCentroid,
  getTradeControlsDataKeyHorizon,
  getTradeControlsDataKeyMT4,
  getTradeControlsDataKeyMT5,
  getTradeLimitPricesDataKeyHorizon,
  getTradeLimitPricesDataKeyMetatrader,
  getTradeStopLimitPricesDataKeyHorizon,
  getTradeStopLimitPricesDataKeyMetatrader,
  getTradeStopPricesDataKeyHorizon,
  getTradeStopPricesDataKeyMetatrader
} from './constants';
import { EUIBuySellType, EUIOrderTypeLabel, IUITradeFormData, TUITradeConfirmationItemData } from './types';

export const checkIsDerivativeSymbol = (symbolCategory?: ESymbolCategory): boolean => {
  return symbolCategory === ESymbolCategory.OPTION || symbolCategory === ESymbolCategory.FUTURES;
};

export const checkIsMarketOrStopOrder = (orderTypeLabel?: EUIOrderTypeLabel): boolean => {
  return orderTypeLabel === EUIOrderTypeLabel.MARKET || orderTypeLabel === EUIOrderTypeLabel.STOP;
};

export const checkIsDerivativeMarketOrStopOrder = (
  symbolCategory?: ESymbolCategory,
  orderTypeLabel?: EUIOrderTypeLabel
): boolean => {
  if (orderTypeLabel === undefined) {
    return false;
  }
  return checkIsDerivativeSymbol(symbolCategory) && checkIsMarketOrStopOrder(orderTypeLabel);
};

export const getTradeBasePricesDataKey = (): TUITradeConfirmationItemData[] => {
  const integration = companyService.getCompanyIntegration();
  if (integration === EMainProductCodeType.METATRADER4) {
    return getTradeBasePricesDataKeyMT4();
  }
  if (integration === EMainProductCodeType.METATRADER5) {
    return getTradeBasePricesDataKeyMT5();
  }
  if (integration === EMainProductCodeType.CENTROID) {
    return getTradeBasePricesDataKeyCentroid();
  }
  return getTradeBasePricesDataKeyHorizon();
};

export const getTradeLimitPricesDataKey = (): TUITradeConfirmationItemData[] => {
  const integration = companyService.getCompanyIntegration();
  const baseData = getTradeBasePricesDataKey();

  if (integration === EMainProductCodeType.HORIZON) {
    return [...baseData, ...getTradeLimitPricesDataKeyHorizon()];
  }
  return [...baseData, ...getTradeLimitPricesDataKeyMetatrader()];
};

export const getTradeStopPricesDataKey = (): TUITradeConfirmationItemData[] => {
  const integration = companyService.getCompanyIntegration();
  const baseData = getTradeBasePricesDataKey();

  if (integration === EMainProductCodeType.HORIZON) {
    return [...baseData, ...getTradeStopPricesDataKeyHorizon()];
  }
  return [...baseData, ...getTradeStopPricesDataKeyMetatrader()];
};

export const getTradeStopLimitPricesDataKey = (): TUITradeConfirmationItemData[] => {
  const integration = companyService.getCompanyIntegration();
  const baseData = getTradeBasePricesDataKey();

  if (integration === EMainProductCodeType.HORIZON) {
    return [...baseData, ...getTradeStopLimitPricesDataKeyHorizon()];
  }
  return [...baseData, ...getTradeStopLimitPricesDataKeyMetatrader()];
};

const shouldShowExpiration = (
  integration: EMainProductCodeType = EMainProductCodeType.HORIZON,
  formValues: IUITradeFormData,
  isDerivativeMarketOrStopOrder?: boolean
): boolean => {
  if (integration === EMainProductCodeType.HORIZON) {
    return formValues['fillPolicy'] === EFillPolicy.GTD && !isDerivativeMarketOrStopOrder;
  }
  return (
    (formValues['tradeExpiry'] === EOrderExpiry.SPECIFIED_DAY ||
      formValues['tradeExpiry'] === EOrderExpiry.SPECIFIED_TIMESTAMP) &&
    !isDerivativeMarketOrStopOrder
  );
};

export const getTradeControlDataKey = (
  formValues: IUITradeFormData,
  isDerivativeMarketOrStopOrder?: boolean,
  tradeConfirmationOrderType?: EOrderType
): TUITradeConfirmationItemData[] => {
  const integration = companyService.getCompanyIntegration();

  const isMarket = tradeConfirmationOrderType === EOrderType.BUY || tradeConfirmationOrderType === EOrderType.SELL;

  let controls: TUITradeConfirmationItemData[];
  switch (integration) {
    case EMainProductCodeType.HORIZON:
      controls = getTradeControlsDataKeyHorizon();
      break;
    case EMainProductCodeType.METATRADER5:
      controls = isMarket
        ? getTradeControlsDataKeyMT5().filter((item) => item.key !== 'tradeExpiry')
        : getTradeControlsDataKeyMT5();
      break;
    case EMainProductCodeType.CENTROID:
      controls = isMarket
        ? getTradeControlsDataKeyCentroid().filter(
            (item) => item.key !== 'tradeExpiry' && item.key !== 'centroidDeviation'
          )
        : getTradeControlsDataKeyCentroid();
      break;
    default:
      controls = isMarket
        ? getTradeControlsDataKeyMT4().filter((item) => item.key !== 'tradeExpiry')
        : getTradeControlsDataKeyMT4();
      break;
  }

  // Update the expiration label dynamically
  controls = controls.map((item) => {
    if (item.key === 'expiration' && formValues.tradeExpiry === EOrderExpiry.SPECIFIED_TIMESTAMP) {
      return { ...item, label: () => i18n.t('Expiry Date & Time') };
    }
    return item;
  });

  return shouldShowExpiration(integration, formValues, isDerivativeMarketOrStopOrder)
    ? controls
    : controls.filter((item) => item.key !== 'expiration');
};

export const getSizeShortLabelByCompany = (): string => {
  const integration = companyService.getCompanyIntegration();
  if (integration === EMainProductCodeType.CENTROID || integration === EMainProductCodeType.METATRADER5) {
    return i18n.t('Vol');
  }
  if (integration === EMainProductCodeType.METATRADER4) {
    return i18n.t('Size');
  }
  return i18n.t('Qty');
};

export const mapTradeConfirmationPriceSection = (orderType?: EOrderType): TUITradeConfirmationItemData[] => {
  const mappedData: Record<EOrderType, TUITradeConfirmationItemData[]> = {
    [EOrderType.BUY]: getTradeBasePricesDataKey(),
    [EOrderType.SELL]: getTradeBasePricesDataKey(),
    [EOrderType.BUY_LIMIT]: getTradeLimitPricesDataKey(),
    [EOrderType.SELL_LIMIT]: getTradeLimitPricesDataKey(),
    [EOrderType.BUY_STOP]: getTradeStopPricesDataKey(),
    [EOrderType.SELL_STOP]: getTradeStopPricesDataKey(),
    [EOrderType.BUY_STOP_LIMIT]: getTradeStopLimitPricesDataKey(),
    [EOrderType.SELL_STOP_LIMIT]: getTradeStopLimitPricesDataKey(),
    [EOrderType.BUY_MARKET_TO_LIMIT]: getTradeBasePricesDataKey(),
    [EOrderType.SELL_MARKET_TO_LIMIT]: getTradeBasePricesDataKey(),
    [EOrderType.BALANCE]: [],
    [EOrderType.CREDIT]: [],
    [EOrderType.CLOSE_BY]: []
  };
  return orderType !== undefined ? mappedData[orderType] : [];
};

export const formatPrice = (value: string, decimal: number): string => {
  const bn = new BigNumber(value);
  const formatted = bn.toFixed(decimal);
  return new BigNumber(formatted).toString();
};

const calculateBuyLimitRefPrice = (ask: number | undefined, stopsLevelBN: BigNumber) => ({
  refPriceBN: ask ? new BigNumber(ask).minus(stopsLevelBN) : undefined,
  isMinimum: false
});

const calculateSellLimitRefPrice = (bid: number | undefined, stopsLevelBN: BigNumber) => ({
  refPriceBN: bid ? stopsLevelBN.plus(bid) : undefined,
  isMinimum: true
});

const calculateBuyStopRefPrice = (ask: number | undefined, stopsLevelBN: BigNumber) => ({
  refPriceBN: ask ? stopsLevelBN.plus(ask) : undefined,
  isMinimum: true
});

const calculateSellStopRefPrice = (bid: number | undefined, stopsLevelBN: BigNumber) => ({
  refPriceBN: bid ? new BigNumber(bid).minus(stopsLevelBN) : undefined,
  isMinimum: false
});

export const calculatePriceAdjustmentRefPrice = (
  orderType: EOrderType | null,
  symbolDetail: Partial<ISymbolDetail>
): { refPriceBN: BigNumber | undefined; isMinimum: boolean } => {
  if (!symbolDetail || !orderType) {
    return { refPriceBN: undefined, isMinimum: false };
  }

  const { ask, bid, stopsLevel = 10 } = symbolDetail;
  const pointStep = symbolDetail.pointStep || 0.0001;
  const stopsLevelBN = new BigNumber(stopsLevel).times(pointStep);

  switch (orderType) {
    case EOrderType.BUY_LIMIT:
      return calculateBuyLimitRefPrice(ask, stopsLevelBN);
    case EOrderType.SELL_LIMIT:
      return calculateSellLimitRefPrice(bid, stopsLevelBN);
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_STOP_LIMIT:
      return calculateBuyStopRefPrice(ask, stopsLevelBN);
    case EOrderType.SELL_STOP:
    case EOrderType.SELL_STOP_LIMIT:
      return calculateSellStopRefPrice(bid, stopsLevelBN);
    default:
      return { refPriceBN: undefined, isMinimum: false };
  }
};

const calculatePriceWithRefValidation = (
  currentPrice: string,
  refPriceBN: BigNumber,
  isMinimum: boolean,
  isIncrement: boolean,
  pointStep: number
): BigNumber => {
  const nextValueBN = isIncrement
    ? new BigNumber(currentPrice).plus(pointStep)
    : new BigNumber(currentPrice).minus(pointStep);

  if ((isMinimum && nextValueBN.gte(refPriceBN)) || (!isMinimum && nextValueBN.lte(refPriceBN))) {
    return nextValueBN;
  }
  return refPriceBN;
};

const calculateSimplePriceAdjustment = (
  currentPrice: string,
  isIncrement: boolean,
  pointStep: number
): BigNumber | undefined => {
  const nextValueBN = isIncrement
    ? new BigNumber(currentPrice).plus(pointStep)
    : new BigNumber(currentPrice).minus(pointStep);
  return nextValueBN.isPositive() ? nextValueBN : undefined;
};

export const calculateAdjustedPrice = (
  currentPrice: string | undefined,
  refPriceBN: BigNumber | undefined,
  isMinimum: boolean,
  isIncrement: boolean,
  pointStep: number
): BigNumber | undefined => {
  if (currentPrice && refPriceBN) {
    return calculatePriceWithRefValidation(currentPrice, refPriceBN, isMinimum, isIncrement, pointStep);
  }

  if (currentPrice) {
    return calculateSimplePriceAdjustment(currentPrice, isIncrement, pointStep);
  }

  return refPriceBN || (isIncrement ? new BigNumber(pointStep) : undefined);
};

export const calculatePriceTriggerRefPrice = (
  buySellType: EUIBuySellType,
  price: string,
  stopsLevel: number,
  pointStep: number
): { refPriceBN: BigNumber; isMinimum: boolean } => {
  const stopsLevelBN = new BigNumber(stopsLevel).times(pointStep);

  if (buySellType === EUIBuySellType.BUY) {
    return {
      refPriceBN: new BigNumber(price).minus(stopsLevelBN),
      isMinimum: false
    };
  } else {
    return {
      refPriceBN: stopsLevelBN.plus(price),
      isMinimum: true
    };
  }
};
